// src/components/SEOAdminShopify.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const SEOAdminShopify = () => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shopData, setShopData] = useState(null);
  const [shopDomain, setShopDomain] = useState('');
  const [currentShop, setCurrentShop] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    checkConnectionStatus();
    
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('connected') === 'true') {
      setConnected(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/connection-status', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to check connection status');
      }
      
      const data = await response.json();
      setConnected(data.connected);
      if (data.shop) {
        setCurrentShop(data.shop);
      }
    } catch (err) {
      console.error('Error checking connection status:', err);
      setError(err.message);
    }
  };

  const handleConnect = () => {
    if (!shopDomain.trim()) {
      setError('Please enter your shop domain');
      return;
    }
    
    window.location.href = `http://localhost:5000/auth/install?shop=${shopDomain}`;
  };

  const fetchStoreData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log("Fetching store data...");  // Debug log
      const response = await fetch('http://localhost:5000/api/fetch-store-data', {
        method: 'GET',
        credentials: 'include',  // Send cookies
        headers: {
          'Accept': 'application/json',
        }
      });
      
      console.log("Response status:", response.status);  // Debug log
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch store data');
      }
      
      const data = await response.json();
      console.log("Data received:", data);  // Debug log
      setShopData(data);
    } catch (err) {
      console.error('Full fetch error:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const performSEOAnalysis = (data) => {
    const issues = [];
    
    data.products.forEach(product => {
      if (!product.seo_title) {
        issues.push(`Product "${product.title}" missing SEO title`);
      }
      if (!product.seo_description) {
        issues.push(`Product "${product.title}" missing SEO description`);
      }
      if (product.seo_title && product.seo_title.length > 70) {
        issues.push(`Product "${product.title}" SEO title too long (${product.seo_title.length} chars)`);
      }
      if (product.seo_description && product.seo_description.length > 160) {
        issues.push(`Product "${product.title}" SEO description too long (${product.seo_description.length} chars)`);
      }
    });

    data.pages.forEach(page => {
      if (!page.summary_description) {
        issues.push(`Page "${page.title}" missing meta description`);
      }
    });

    data.collections.forEach(collection => {
      if (!collection.seo_title) {
        issues.push(`Collection "${collection.title}" missing SEO title`);
      }
      if (!collection.seo_description) {
        issues.push(`Collection "${collection.title}" missing SEO description`);
      }
    });

    return issues;
  };

  return (
    <div className="min-h-screen w-full relative bg-white pt-24 p-6 overflow-x-hidden">
      <span className='absolute motion-safe:animate-spin-slow opacity-65 -z-1 top-24 -left-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-emerald-400 bg-gradient-to-br from-blue-900 via-purple-950 from-20% to-teal-400'></span>
      <span className='absolute motion-safe:animate-spin-slow opacity-65 -z-1 top-0 -right-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-dark_cyan-100 bg-gradient-to-br from-blue-800 via-purple-400 from-20% to-cyan-300'></span>
      <span className='absolute motion-safe:animate-bounce-slow opacity-65 -z-1 bottom-0 -ml-36 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-emerald-400 bg-gradient-to-br from-teal-800 to-teal-500'></span>
      
      <div className="max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex flex-col items-center"
        >
          <h1 className="bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-transparent bg-clip-text text-6xl font-bold py-5 w-fit">Shopify SEO Auditor</h1>
          <p className="bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-transparent bg-clip-text w-fit text-2xl">
            Audit and optimize your Shopify store's SEO performance
          </p>
        </motion.div>

        <div className="bg-transparent rounded-lg backdrop-blur-md shadow-xl rounded-xll overflow-hidden">
          {!connected ? (
            <div className="p-6 space-y-6">
              <div>
                <h2 className="text-2xl font-semibold mb-4">Connect Your Shopify Store</h2>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="shop-domain" className="block text-sm font-medium mb-2">
                      Your Shopify Domain
                    </label>
                    <input
                      id="shop-domain"
                      type="text"
                      className="w-full bg-gradient-to-br from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 border border-fixify-primary rounded-lg px-4 py-3 focus:ring-2 focus:ring-fixify-lime focus:border-transparent"
                      placeholder="your-shop-name.myshopify.com"
                      value={shopDomain}
                      onChange={(e) => setShopDomain(e.target.value)}
                    />
                    <p className="mt-1 text-sm text-vanilla-300">
                      Enter your Shopify store domain (e.g., mystore.myshopify.com)
                    </p>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={handleConnect}
                      className="lg:w-48 md:w-full text-center w-full px-6 py-3 bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 rounded-lg font-medium transition-all"
                    >
                      Connect Store
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">
                  <span className="text-tiffany_blue-300">✓</span> Connected to {currentShop}
                </h2>
                <button 
                  onClick={fetchStoreData} 
                  disabled={loading}
                  className="lg:w-48 md:w-full text-center w-full px-6 py-3 bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 rounded-lg font-medium flex justify-center items-center space-x-2 transition-all"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Analyzing...
                    </>
                  ) : (
                    'Start SEO Audit'
                  )}
                </button>
              </div>

              {error && (
                <div className="p-4 bg-rufous-500/20 border border-rufous-500 rounded-lg text-rufous-100">
                  {error}
                </div>
              )}

              {shopData && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <h3 className="text-xl font-semibold">SEO Audit Results</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-rich_black-400 p-6 rounded-lg">
                      <h4 className="text-sm font-medium text-vanilla-300 mb-2">Products</h4>
                      <p className="text-3xl font-bold text-tiffany_blue-300">{shopData.products.length}</p>
                    </div>
                    <div className="bg-rich_black-400 p-6 rounded-lg">
                      <h4 className="text-sm font-medium text-vanilla-300 mb-2">Pages</h4>
                      <p className="text-3xl font-bold text-tiffany_blue-300">{shopData.pages.length}</p>
                    </div>
                    <div className="bg-rich_black-400 p-6 rounded-lg">
                      <h4 className="text-sm font-medium text-vanilla-300 mb-2">Collections</h4>
                      <p className="text-3xl font-bold text-tiffany_blue-300">{shopData.collections.length}</p>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-xl overflow-hidden">
  {(() => {
    const issues = performSEOAnalysis(shopData);
    
    return (
      <>
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <h4 className="text-xl font-semibold bg-gradient-to-r from-blue-100 to-cyan-100 text-transparent bg-clip-text">
              SEO Health Analysis
            </h4>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                issues.length > 0 
                  ? 'bg-amber-400/20 text-amber-300' 
                  : 'bg-emerald-400/20 text-emerald-300'
              }`}>
                {issues.length > 0 ? `${issues.length} Issues` : 'All Clear'}
              </span>
              <button className="text-xs bg-white/5 hover:bg-white/10 px-3 py-1 rounded-full transition-all">
                Export Report
              </button>
            </div>
          </div>
          
          <div className="mt-3 flex items-center space-x-4">
            <div className="flex-1 bg-gray-800/50 rounded-full h-2 overflow-hidden">
              <div 
                className={`h-full ${
                  issues.length > 10 ? 'bg-red-400' : 
                  issues.length > 5 ? 'bg-amber-400' : 'bg-emerald-400'
                }`}
                style={{ width: `${Math.min(100 - (issues.length * 2), 100)}%` }}
              ></div>
            </div>
            <span className="text-xs font-mono">
              {Math.max(100 - (issues.length * 2), 0)}/100
            </span>
          </div>
        </div>

        {issues.length > 0 ? (
          <div className="divide-y divide-white/5 max-h-[400px] overflow-y-auto">
            {issues.map((issue, index) => {
              const [entityType, ...messageParts] = issue.split(':');
              const message = messageParts.join(':').trim();
              const entity = entityType.includes('Product') ? 'Product' : 
                            entityType.includes('Page') ? 'Page' : 'Collection';
              
              return (
                <div key={index} className="p-4 hover:bg-white/5 transition-all group">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${
                      issue.includes('missing') ? 'bg-red-400/10' : 'bg-amber-400/10'
                    }`}>
                      {issue.includes('missing') ? (
                        <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <h5 className="font-medium text-white/90">
                          {entityType.trim()}
                        </h5>
                        <span className="text-xs bg-black/30 px-2 py-1 rounded">
                          {entity}
                        </span>
                      </div>
                      
                      <p className="text-sm mt-1 text-white/70">
                        {message}
                      </p>
                      
                      <div className="mt-3 flex space-x-2">
                        <button className="text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full transition-all flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Suggest Fix
                        </button>
                        <button className="text-xs bg-gray-700/50 hover:bg-gray-700/70 px-3 py-1 rounded-full transition-all">
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-400/10 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h5 className="text-lg font-medium text-white/90 mb-1">
              Perfect SEO Health
            </h5>
            <p className="text-sm text-white/60 max-w-md mx-auto">
              All pages, products and collections have proper SEO metadata configured. Great job!
            </p>
          </div>
        )}

        <div className="p-4 bg-black/20 border-t border-white/5">
          <div className="flex justify-between items-center text-xs">
            <span className="text-white/50">
              Last scanned: {new Date().toLocaleString()}
            </span>
            <button 
              onClick={() => fetchStoreData()}
              className="text-cyan-300 hover:text-cyan-200 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Rescan Now
            </button>
          </div>
        </div>
      </>
    );
  })()}
</div>

                  <details className="bg-rich_black-500 bg-opacity-70 backdrop-blur-lg rounded-xl overflow-hidden">
                    <summary className="p-4 cursor-pointer font-medium">View Raw Data</summary>
                    <div className="p-4 bg-rich_black-400 max-h-96 overflow-y-auto">
                      <pre className="text-xs text-vanilla-300">
                        {JSON.stringify(shopData, null, 2)}
                      </pre>
                    </div>
                  </details>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SEOAdminShopify;